import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSidebar } from 'contexts/SidebarContext';
import useCurrentUser from 'hooks/useCurrentUser';
import Alerts from 'pages/Clients/Alerts';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOutUser } from 'redux/slices/user.slice';
import { useGetAlertsQuery } from 'services/api/alerts.api';
import { useLogoutUserMutation } from 'services/api/user.api';
import { posthogReset } from 'utils/posthogUtils';

import { glassMenuProps, NAVY, YELLOW } from './sidebar.styles';

const SidebarFooter: React.FC = () => {
  const { collapsed, selectedBusinessUnitId, setSelectedBusinessUnitId } = useSidebar();
  const {
    businessUnits,
    isLoading: isLoadingBusinessUnits,
    getBusinessUnitsByCompany,
    companyNames,
  } = useBusinessUnits();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [logoutUser] = useLogoutUserMutation();

  const isTicketsView = location.pathname.startsWith('/tickets');

  const [avatarAnchorEl, setAvatarAnchorEl] = useState<null | HTMLElement>(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const alertsAnchorRef = useRef<HTMLDivElement>(null);
  const [buFilter, setBuFilter] = useState('');
  const [buSelectOpen, setBuSelectOpen] = useState(false);
  const buFilterRef = useRef<HTMLInputElement>(null);

  const isAdmin = !!currentUser?.isAdmin;

  // Alerts
  const { data: alerts, refetch: refetchAlerts } = useGetAlertsQuery(null, {
    skip: isAdmin,
  });
  const unreadAlertCount = alerts?.filter((a) => a.readAt === null).length ?? 0;

  const normalizedFilter = buFilter.toLowerCase();
  const filteredBusinessUnits = businessUnits?.filter((bu) => bu.name.toLowerCase().includes(normalizedFilter)) ?? [];
  const filteredCompanyNames = companyNames
    .sort((a, b) => a.localeCompare(b))
    .filter(
      (company) =>
        company.toLowerCase().includes(normalizedFilter) ||
        getBusinessUnitsByCompany(company).some((bu) => bu.name.toLowerCase().includes(normalizedFilter))
    );

  // Flat ordered list of selectable items for keyboard navigation
  const selectableIds: number[] = [];
  if (isAdmin && !normalizedFilter) selectableIds.push(-1);
  if (isAdmin) {
    filteredCompanyNames.forEach((company) => {
      getBusinessUnitsByCompany(company)
        .filter(
          (bu) => bu.name.toLowerCase().includes(normalizedFilter) || company.toLowerCase().includes(normalizedFilter)
        )
        .forEach((bu) => selectableIds.push(bu.id));
    });
  } else {
    filteredBusinessUnits.forEach((bu) => selectableIds.push(bu.id));
  }

  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const getBuMenuItemSx = (idx: number, extra?: object) => ({
    fontSize: '13px',
    borderRadius: '8px',
    mx: 1,
    backgroundColor: highlightedIndex === idx ? alpha(NAVY, 0.08) : 'transparent',
    '&.Mui-focusVisible': { backgroundColor: highlightedIndex === idx ? alpha(NAVY, 0.08) : 'transparent' },
    '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
    '&.Mui-selected': { backgroundColor: alpha(YELLOW, 0.18) },
    '&.Mui-selected:hover': { backgroundColor: alpha(YELLOW, 0.24) },
    '&.Mui-selected.Mui-focusVisible': { backgroundColor: alpha(YELLOW, 0.18) },
    ...extra,
  });

  // Reset highlight when filter changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Needed to reset the selected BU
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [buFilter]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && buSelectOpen) {
      document.querySelector(`[data-bu-index="${highlightedIndex}"]`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, buSelectOpen]);

  const handleFilterKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, selectableIds.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      setSelectedBusinessUnitId(selectableIds[highlightedIndex]);
      setBuSelectOpen(false);
      setBuFilter('');
      setHighlightedIndex(-1);
    } else if (e.key === 'Escape') {
      setBuSelectOpen(false);
      setBuFilter('');
      setHighlightedIndex(-1);
    }
  };

  const userInitials = currentUser?.name
    ? currentUser.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '';

  const handleLogout = () => {
    setAvatarAnchorEl(null);
    posthogReset();
    // Fire-and-forget: revokes the refresh-token family server-side so the session is
    // dead even if this device's storage is later read. Local sign-out proceeds regardless.
    logoutUser();
    dispatch(signOutUser());
    navigate('/');
  };

  const getBusinessUnitSelectTitle = (): string | undefined => {
    if (!collapsed || !businessUnits || businessUnits.length === 0) return undefined;
    if (selectedBusinessUnitId === null || selectedBusinessUnitId === undefined)
      return 'Unidad de negocio — seleccionar';
    if (selectedBusinessUnitId === -1) return 'Todas las unidades';
    return businessUnits.find((b) => b.id === selectedBusinessUnitId)?.name ?? 'Unidad de negocio';
  };
  const businessUnitSelectTitle = getBusinessUnitSelectTitle();

  return (
    <Box sx={{ px: collapsed ? 1 : 2, py: 2, backgroundColor: alpha(NAVY, 0.03) }}>
      {/* Business Unit Selector — loading placeholder reserves slot so selector doesn't disappear during fetch */}
      {isLoadingBusinessUnits && (!businessUnits || businessUnits.length === 0) && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 1,
              px: collapsed ? 0 : 1.5,
              py: 1,
              minHeight: 40,
              borderRadius: '16px',
              backgroundColor: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${alpha(NAVY, 0.08)}`,
            }}
            aria-busy="true"
            aria-label="Cargando unidades de negocio"
          >
            <CircularProgress size={14} sx={{ color: NAVY }} />
            {!collapsed && (
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: alpha(NAVY, 0.6) }}>
                Cargando unidades…
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {businessUnits && businessUnits.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title={
              isTicketsView
                ? 'La vista de Tickets es general de la plataforma: muestra todos los tickets a los que tienes acceso. Usa el filtro de unidad de negocio dentro de la vista para acotar los resultados.'
                : ''
            }
            placement="right"
            arrow
            disableHoverListener={!isTicketsView}
            disableFocusListener={!isTicketsView}
            disableTouchListener={!isTicketsView}
          >
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              <Select
                value={selectedBusinessUnitId ?? ''}
                onChange={(e) => setSelectedBusinessUnitId(e.target.value as number)}
                displayEmpty
                fullWidth
                size="small"
                disabled={isTicketsView}
                inputProps={{
                  title: businessUnitSelectTitle,
                  'aria-label': businessUnitSelectTitle ?? 'Unidad de negocio',
                }}
                sx={{
                  borderRadius: '16px',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(12px)',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: NAVY,
                  '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(NAVY, 0.08)}` },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(NAVY, 0.15)}` },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(YELLOW, 0.6)}` },
                  ...(collapsed && {
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 0.75,
                      pl: 0.5,
                      pr: '28px !important',
                      minHeight: '40px !important',
                    },
                    '& .MuiSelect-icon': { right: 2, color: alpha(NAVY, 0.45) },
                  }),
                }}
                open={buSelectOpen}
                onOpen={() => {
                  setBuSelectOpen(true);
                  setTimeout(() => buFilterRef.current?.focus(), 0);
                }}
                onClose={() => {
                  setBuSelectOpen(false);
                  setBuFilter('');
                }}
                MenuProps={{
                  ...glassMenuProps,
                  ...(collapsed
                    ? {
                        anchorOrigin: { vertical: 'center', horizontal: 'right' },
                        transformOrigin: { vertical: 'center', horizontal: 'left' },
                      }
                    : {}),
                }}
                renderValue={(value) => {
                  let textLabel = 'Unidad de Negocio';
                  if (value === -1) textLabel = 'Todas las unidades';
                  else if (value && value !== -1)
                    textLabel = businessUnits.find((b) => b.id === value)?.name || 'Unidad de Negocio';

                  if (collapsed) {
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 22, color: NAVY }} aria-hidden />
                      </Box>
                    );
                  }
                  return textLabel;
                }}
              >
                <ListSubheader
                  sx={{ px: 1.5, pt: 1, pb: 0.5, backgroundColor: 'transparent', lineHeight: 'unset' }}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <InputBase
                    inputRef={buFilterRef}
                    placeholder="Buscar empresa o unidad..."
                    value={buFilter}
                    onChange={(e) => setBuFilter(e.target.value)}
                    onKeyDown={handleFilterKeyDown}
                    onBlur={() => {
                      if (buSelectOpen) setTimeout(() => buFilterRef.current?.focus(), 0);
                    }}
                    fullWidth
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: alpha(NAVY, 0.4) }} />
                      </InputAdornment>
                    }
                    sx={{
                      fontSize: '13px',
                      color: NAVY,
                      backgroundColor: alpha(NAVY, 0.04),
                      borderRadius: '10px',
                      px: 1,
                      py: 0.5,
                      mb: 0.5,
                      '& input': { py: 0 },
                      '& input::placeholder': { color: alpha(NAVY, 0.4), opacity: 1 },
                    }}
                  />
                </ListSubheader>
                {isAdmin && !normalizedFilter && (
                  <MenuItem value={-1} data-bu-index={0} sx={getBuMenuItemSx(0, { fontWeight: 600 })}>
                    Todas las unidades de negocio
                  </MenuItem>
                )}
                {isAdmin
                  ? filteredCompanyNames.flatMap((company: string) => [
                      <ListSubheader
                        key={company}
                        sx={{
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: alpha(NAVY, 0.5),
                          backgroundColor: 'rgba(249, 250, 253, 0.97)',
                          lineHeight: '32px',
                        }}
                      >
                        {company}
                      </ListSubheader>,
                      ...(getBusinessUnitsByCompany(company)
                        .filter(
                          (bu) =>
                            bu.name.toLowerCase().includes(normalizedFilter) ||
                            company.toLowerCase().includes(normalizedFilter)
                        )
                        .map((bu) => {
                          const idx = selectableIds.indexOf(bu.id);
                          return (
                            <MenuItem
                              key={bu.id}
                              value={bu.id}
                              data-bu-index={idx}
                              sx={getBuMenuItemSx(idx, { pl: 3 })}
                            >
                              {bu.name}
                            </MenuItem>
                          );
                        }) as React.ReactElement[]),
                    ])
                  : filteredBusinessUnits.map((bu) => {
                      const idx = selectableIds.indexOf(bu.id);
                      return (
                        <MenuItem key={bu.id} value={bu.id} data-bu-index={idx} sx={getBuMenuItemSx(idx)}>
                          {bu.name}
                        </MenuItem>
                      );
                    })}
              </Select>
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Avatar */}
      <Box
        ref={alertsAnchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          padding: collapsed ? '4px' : '8px',
          borderRadius: '16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          transition: 'background-color 0.2s ease',
          '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
        }}
        onClick={(e) => setAvatarAnchorEl(e.currentTarget)}
      >
        <Avatar sx={{ width: 36, height: 36, backgroundColor: NAVY, color: '#fff', fontSize: '14px', fontWeight: 600 }}>
          {userInitials || <PersonIcon />}
        </Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: NAVY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {currentUser?.name || 'Usuario'}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: alpha(NAVY, 0.5) }}>
              {(
                {
                  admin: 'Administrador',
                  businessUnitAdmin: 'Jefe de Sucursal',
                  branchManager: 'Gerente',
                  seller: 'Vendedor',
                  sellerWithAssignment: 'Vendedor',
                } as Record<string, string>
              )[currentUser?.role ?? ''] ??
                currentUser?.role ??
                ''}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Avatar Menu */}
      <Menu
        anchorEl={avatarAnchorEl}
        open={Boolean(avatarAnchorEl)}
        onClose={() => setAvatarAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        {...glassMenuProps}
      >
        <MenuItem
          onClick={() => {
            setAvatarAnchorEl(null);
            navigate('/profile');
          }}
          sx={{ borderRadius: '8px', mx: 1, gap: 1.5, fontSize: '14px' }}
        >
          <PersonIcon fontSize="small" /> Perfil
        </MenuItem>
        {!isAdmin && (
          <MenuItem
            onClick={() => {
              setAvatarAnchorEl(null);
              setAlertsOpen(true);
            }}
            sx={{ borderRadius: '8px', mx: 1, gap: 1.5, fontSize: '14px' }}
          >
            <Badge
              badgeContent={unreadAlertCount}
              color="primary"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
            >
              <NotificationsNoneIcon fontSize="small" />
            </Badge>
            Alertas
          </MenuItem>
        )}
        <MenuItem
          onClick={handleLogout}
          sx={{ borderRadius: '8px', mx: 1, gap: 1.5, fontSize: '14px', color: 'error.main' }}
        >
          <LogoutIcon fontSize="small" /> Cerrar sesión
        </MenuItem>
      </Menu>

      {/* Alerts popover (anchored to avatar area) */}
      {!isAdmin && (
        <Alerts
          alerts={alerts || []}
          refetchAlerts={refetchAlerts}
          externalOpen={alertsOpen}
          externalAnchorEl={alertsAnchorRef.current}
          onExternalClose={() => setAlertsOpen(false)}
        />
      )}
    </Box>
  );
};

export default SidebarFooter;
